import React, { FunctionComponent, useState, useEffect } from 'react';
import ReactMapGL, { Marker, Popup, NavigationControl } from 'react-map-gl';
import { Grid, Icon } from 'semantic-ui-react';
import withStyles from 'react-jss';
import classnames from 'classnames';

import { Place } from 'scenes/Home/scenes/MerchantVenuesOnboarding/MerchantVenuesOnboarding.helpers';
import { getStateViewportPreset } from 'helpers/USMapStates';
import { useSelectedPlaces } from 'helpers/useSelectedPlaces';
import { ClassProp } from 'helpers/types';
import { Theme } from 'config';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Viewport = {
  width: string | number;
  height: string | number;
  latitude: number;
  longitude: number;
  zoom: number;
};

type Props = {
  classes: Styles;
  places: Place[];
  stateFilter?: string;
  viewport: Viewport;
};

const styles = (theme: Theme) => ({
  popupContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '.5rem',
  },
  popupHeaderContainer: {
    paddingRight: '.625rem',
  },
  popupHeader: {
    color: '#999',
    fontSize: '.875rem',
  },
  popupText: {
    margin: '0',
    fontSize: '.875rem',
  },
  mapMarker: {
    color: `${theme.softViolet}`,
    transform: 'translate(-.625rem,-1.25rem)',
    zIndex: 0,
    cursor: 'pointer',
    opacity: '.75',
  },
  selectedPlace: {
    color: `${theme.violet}`,
    opacity: '.95',
  },
  navigationControls: {
    position: 'absolute',
    top: '36',
    left: '0',
    padding: '.625rem',
  },
});

const initViewport: Viewport = {
  width: '100%',
  height: 400,
  latitude: 40,
  longitude: -96,
  zoom: 3,
};

const renderPopUp = (classes: Styles, place: Place, togglePopUp: Function) => {
  if (!place) return null;

  const {
    geocode: { latitude, longitude },
    name,
    address: { street, city, state, postal },
  } = place;

  return (
    <Popup
      latitude={latitude}
      longitude={longitude}
      closeButton
      closeOnClick={false}
      onClose={togglePopUp}
      dynamicPosition={false}
      anchor="left"
      offsetLeft={15}
      offsetTop={-10}
      altitude={1}
    >
      <div className={classes.popupContainer}>
        <div className={classes.popupHeaderContainer}>
          <p className={classes.popupHeader}>Address:</p>
        </div>
        <div>
          <p className={classes.popupText}>{name}</p>
          <p className={classes.popupText}>{street}</p>
          <p className={classes.popupText}>{`${city}, ${state} ${postal}`}</p>
        </div>
      </div>
    </Popup>
  );
};

const getMapMarkers = (
  places: Place[],
  selectedPlaces: string[],
  openPopup: Function,
  classes: Styles,
) =>
  places.map((place) => {
    const {
      geocode: { longitude, latitude },
      externalId,
    } = place;
    if (longitude && latitude) {
      return (
        <Marker longitude={longitude} latitude={latitude} key={externalId}>
          <Icon
            name="map marker alternate"
            size="large"
            className={classnames(classes.mapMarker, {
              [classes.selectedPlace]: selectedPlaces.includes(externalId),
            })}
            onClick={openPopup(place)}
          />
        </Marker>
      );
    }
  });

const getGeoLocation = (state: string, viewport: Viewport) => {
  const viewportPreset = getStateViewportPreset(state);
  if (viewportPreset) {
    return {
      ...viewport,
      ...viewportPreset,
    };
  }
  return initViewport;
};

const PlacesMap: FunctionComponent<Props> = ({
  classes,
  places,
  stateFilter,
  viewport,
}) => {
  const [popupContent, setPopupContent] = useState<Place | null>(null);
  const [mapViewport, setMapViewport] = useState<Viewport>(initViewport);
  const { selectedPlaces } = useSelectedPlaces();

  const updateViewport = (updatedViewport: Viewport) =>
    setMapViewport(updatedViewport);

  const closePopup = () => {
    setPopupContent(null);
  };

  const openPopup = (place: Place) => () => {
    setPopupContent(place);
  };

  useEffect(() => {
    closePopup();
  }, [places.length]);

  useEffect(() => {
    if (stateFilter) {
      setMapViewport(getGeoLocation(stateFilter, viewport));
    } else {
      setMapViewport(initViewport);
    }
  }, [stateFilter]);

  useEffect(() => {
    if (stateFilter) {
      setMapViewport(getGeoLocation(stateFilter, viewport));
    } else {
      setMapViewport(viewport);
    }
  }, []);

  return (
    <Grid.Row>
      <Grid.Column width={8} floated="right">
        <ReactMapGL
          {...mapViewport}
          onViewportChange={updateViewport}
          mapStyle="mapbox://styles/mapbox/light-v10"
          minZoom={1}
          onClick={closePopup}
          scrollZoom={false}
        >
          {getMapMarkers(places, selectedPlaces, openPopup, classes)}
          {renderPopUp(classes, popupContent as Place, closePopup)}
          <div className={classes.navigationControls}>
            <NavigationControl showCompass={false} />
          </div>
        </ReactMapGL>
      </Grid.Column>
    </Grid.Row>
  );
};

export default withStyles(styles)(PlacesMap);
